import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid } from '@material-ui/core';
import ContentLayout from 'components/common/layouts/ContentLayout';
import InvoiceInformation from 'components/plansales/billing/detail/information/InvoiceInformation';
import ActionButtons from 'components/plansales/billing/detail/ActionButtons';
import RefundModal, {
  RefundModalDowngradeActionType,
} from 'components/plansales/billing/detail/RefundModal';
import { InvoiceDescriptionStatus } from 'types/plansales/billing';
import { billingActions, selectRefundState } from 'features/billing/billingSlice';
import { selectLoginUserInfo } from 'features/auth/authSlice';
import InformModal from 'components/common/modal/InformModal';
import BillingAddressInformation from 'components/plansales/billing/detail/information/BillingAddressInformation';
import { useHistory } from 'react-router-dom';
import { GetInvoiceDetailResponse } from 'types/plansales/invoice/invoiceDetail';
import { useInvoiceSummary } from 'modules/billing/useInvoiceSummary';
import InvoiceDetailMessage from './InvoiceDetailMessage';
import { getInvoiceSemanticsStatus } from 'modules/billing/invoiceStatus';
import { GroupPlanType } from 'types/plansales/plan';
import { NULL_TEXT } from 'constants/common';
import InvoiceDetailItemList from './InvoiceDetailItemList';
import { groupPlanMap } from 'constants/plansales/billing';
import InvoiceDetailBackOrderInformation from './InvoiceDetailBackOrderInformation';
import InvoiceDetailPaymentInformation from './InvoiceDetailPaymentInformation';
import { fetchInvoiceDetail } from 'api/plansales/invoice';
import { InvoiceDetailStatus } from '../../../../types/plansales/billing/invoice';

interface Props {
  invoiceNo: string;
}

/**
 * clo-set invoice detail page
 * (invoice history 에서 진입)
 * @param invoiceNo
 * @constructor
 */
const InvoiceDetailContainer = ({ invoiceNo }: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const loginUserInfo = useSelector(selectLoginUserInfo);

  const [refundDowngradeActionType, setRefundDowngradeActionType] =
    useState<RefundModalDowngradeActionType>('freePlan');
  const [invoiceDetailResponse, setInvoiceDetailResponse] =
    useState<GetInvoiceDetailResponse | null>(null);
  const [isLoadingInvoiceDetail, setIsLoadingInvoiceDetail] = useState<boolean>(false);
  const [errorString, setErrorString] = useState<string>('');

  // 페이지 중간의 Invoice Message 항목 (closet-next invoice pag 와 동일한 문구)
  const invoiceMessage = useMemo(() => {
    if (!invoiceDetailResponse) return null;
    return useInvoiceSummary({
      groupName: invoiceDetailResponse.group.name,
      planType: invoiceDetailResponse.planType,
      detail: invoiceDetailResponse.detail,
      items: invoiceDetailResponse.groupInvoiceItems,
      amount: invoiceDetailResponse.amount,
    });
  }, [invoiceDetailResponse]);

  // legacy refund api
  const [refundModalOpen, setRefundModalOpen] = useState<boolean>(false);
  const [refundErrorModalOpen, setRefundErrorModalOpen] = useState<boolean>(false);
  const { error: refundError, refresh } = useSelector(selectRefundState);

  const isPending =
    invoiceDetailResponse?.status === InvoiceDetailStatus.Draft ||
    invoiceDetailResponse?.status === InvoiceDetailStatus.Open;

  const isRefunded = invoiceDetailResponse?.status === InvoiceDetailStatus.Refund;

  const isVoid = invoiceDetailResponse?.status === InvoiceDetailStatus.Void;

  const cannotRefund =
    invoiceDetailResponse?.planType === GroupPlanType.ADVANCED_PLAN_INV || isPending;

  const invoiceStatus = useMemo(() => {
    if (!invoiceDetailResponse) return InvoiceDescriptionStatus.NO_CASE;
    return getInvoiceSemanticsStatus(
      invoiceDetailResponse.planType,
      invoiceDetailResponse.groupInvoiceItems,
      invoiceDetailResponse.detail,
      invoiceDetailResponse.amount.total,
    );
  }, [invoiceDetailResponse]);

  const init = (): void => {
    setErrorString('');
    fetchInvoiceDetail({ invoiceId: invoiceNo })
      .then(data => setInvoiceDetailResponse(data))
      .catch(e => setErrorString(JSON.stringify(e)));
  };

  useEffect(() => {
    if (refundError.length > 0) {
      setRefundErrorModalOpen(true);
      setRefundModalOpen(false);
    }
  }, [refundError]);

  useEffect(() => {
    init();
  }, []);

  // TODO: refund 기능 붙이면서 재작업
  // useEffect(() => {
  //   if (refresh) {
  //     history.go(0);
  //   } // reload
  // }, [refresh]);

  const onClickCloseErrorButton = () => {
    setRefundErrorModalOpen(false);
    dispatch(billingActions.resetRefundState());
  };

  if (errorString)
    return (
      <p>
        Cannot get this invoice data. Please contact the admin. Ted & Benny <br />
        <br />
        {errorString}
      </p>
    );
  if (!invoiceDetailResponse) return <></>;
  return (
    <>
      <ContentLayout pageTitle={'Invoice Detail'} loading={isLoadingInvoiceDetail}>
        <Grid container>
          <ActionButtons
            userId={-1}
            setRefundModalOpen={setRefundModalOpen}
            requestRefundLoading={isLoadingInvoiceDetail}
            // TODO: TED refund 기능은 가장 마지막에 만들어주실 예정
            refundDisabled={true}
            groupId={invoiceDetailResponse.groupId}
          />
        </Grid>
        <Grid container spacing={3}>
          <Grid item sm={12} md={6}>
            <Grid container direction={'column'}>
              <Grid item>
                <InvoiceInformation
                  groupId={invoiceDetailResponse.groupId}
                  groupName={invoiceDetailResponse.group.name}
                  refundRequestedBy={invoiceDetailResponse.registeredDate}
                  adminEmail={invoiceDetailResponse.group.adminEmail}
                  invoiceNo={invoiceDetailResponse.invoiceId}
                  last4CardNumber={invoiceDetailResponse.paymentInfo.last4CardNo}
                  paymentMethod={invoiceDetailResponse.paymentMethod}
                  paymentId={invoiceDetailResponse.paymentInfo.paymentTaxId || NULL_TEXT}
                  avalaraDocCode={invoiceDetailResponse.paymentInfo.avalaraDocCode || NULL_TEXT}
                  paymentStatus={invoiceDetailResponse.status}
                  registeredDate={invoiceDetailResponse.registeredDate}
                  paymentSucceedDate={invoiceDetailResponse.detail.succeedDate}
                  paymentRefundDate={invoiceDetailResponse.detail.refundDate}
                  isPending={isPending}
                  isRefunded={isRefunded}
                  isVoid={isVoid}
                  planType={invoiceDetailResponse.planType}
                  invoiceStatus={invoiceStatus}
                  paymentInvoiceId={invoiceDetailResponse.paymentInfo.paymentInvoiceId}
                  taxAmount={invoiceDetailResponse.amount.tax}
                  refetchInvoice={init}
                />
              </Grid>
              <Grid item>
                <InvoiceDetailPaymentInformation data={invoiceDetailResponse} />
              </Grid>
            </Grid>
          </Grid>
          <Grid item sm={12} md={6}>
            <Grid container direction={'column'}>
              <Grid item>
                <InvoiceDetailBackOrderInformation data={invoiceDetailResponse.backOrderInfo} />
              </Grid>
              <Grid item>
                <BillingAddressInformation billingAddress={invoiceDetailResponse.billingAddress} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid>
          {invoiceMessage && (
            <InvoiceDetailMessage
              additionalMessage={invoiceMessage.additionalMessage}
              summaryMessage={invoiceMessage.summaryMessage}
            />
          )}
          <InvoiceDetailItemList
            invoiceItems={invoiceDetailResponse.groupInvoiceItems}
            planName={groupPlanMap[invoiceDetailResponse.planType]}
            paymentAmount={invoiceDetailResponse.amount}
            isRefunded={isRefunded}
          />
        </Grid>
        {refundModalOpen && (
          <RefundModal
            invoiceNo={invoiceDetailResponse.invoiceId}
            refundModalOpen={refundModalOpen}
            isRefunded={isRefunded}
            paymentRefundDate={invoiceDetailResponse.detail.refundDate ?? NULL_TEXT}
            loginUserName={loginUserInfo.name}
            setRefundModalOpen={setRefundModalOpen}
            refundErrorModalOpen={refundErrorModalOpen}
            // TODO: ted 께 서 refund 기능이 개발 된 이후에 api 모델에 추가 해주신다고 하심
            refundInformation={null}
            refundDowngradeActionType={refundDowngradeActionType}
            setRefundDowngradeActionType={setRefundDowngradeActionType}
          />
        )}
        <InformModal
          open={refundErrorModalOpen}
          onClose={onClickCloseErrorButton}
          description={refundError}
        />
      </ContentLayout>
    </>
  );
};

export default InvoiceDetailContainer;
